/**
 * Everything the sync client takes from its host (architecture §3.9: framework-agnostic, headless in Node for the
 * simulation). The browser host passes `browserEnv()`; tests pass fakes with virtual time, so every timeout, backoff
 * and heartbeat rule is truth-table tested without real waiting.
 */

export type TimerHandle = object | number;

export interface Timers {
  /** Wall-clock milliseconds (the local clock; `Clock` corrects it with the server's time). */
  now(): number;
  setTimeout(fn: () => void, ms: number): TimerHandle;
  clearTimeout(handle: TimerHandle): void;
  setInterval(fn: () => void, ms: number): TimerHandle;
  clearInterval(handle: TimerHandle): void;
}

/** The subset of `WebSocket` the connection uses. */
export interface SocketLike {
  readonly readyState: number;
  binaryType: string;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number; reason: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}

export type SocketFactory = (url: string, protocols: string[]) => SocketLike;

/** The subset of `fetch` the HTTP client uses. */
export type FetchLike = (url: string, init: RequestInit) => Promise<Response>;

/** Key/value storage (sessionStorage in the browser) for composer text across a model-version reload (§3.8). */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** `BroadcastChannel('quadratic')` messages (§3.14): never data, always the account id. */
export interface Broadcast {
  post(message: { type: string; accountId: string; [k: string]: unknown }): void;
  subscribe(fn: (message: { type: string; accountId: string; [k: string]: unknown }) => void): () => void;
}

/** Page lifecycle signals (visibility, online, pageshow) for the wake detector and spread waits. */
export interface PageSignals {
  isVisible(): boolean;
  isOnline(): boolean;
  /** Subscribe to `visibilitychange`, `online`, `offline` and `pageshow`. */
  on(event: 'visible' | 'hidden' | 'online' | 'offline' | 'pageshow', fn: () => void): () => void;
}

export interface Env {
  readonly timers: Timers;
  readonly fetch: FetchLike;
  readonly socket: SocketFactory;
  readonly page: PageSignals;
  readonly storage: KeyValueStorage;
  readonly broadcast: Broadcast;
  /** Uniform [0, 1); injectable for deterministic jitter. */
  random(): number;
  /** Full page reload (model-version reloads, account switches). */
  reload(): void;
}

/** Default timers over the host globals. */
export function globalTimers(): Timers {
  return {
    now: () => Date.now(),
    setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
    clearTimeout: (h) => globalThis.clearTimeout(h as ReturnType<typeof globalThis.setTimeout>),
    setInterval: (fn, ms) => globalThis.setInterval(fn, ms),
    clearInterval: (h) => globalThis.clearInterval(h as ReturnType<typeof globalThis.setInterval>),
  };
}

const memoryStorage = (): KeyValueStorage => {
  const m = new Map<string, string>();
  return {
    getItem: (k) => m.get(k) ?? null,
    setItem: (k, v) => void m.set(k, v),
    removeItem: (k) => void m.delete(k),
  };
};

/** The browser host. Falls back to in-memory pieces where an API is missing (private mode storage, old browsers). */
export function browserEnv(): Env {
  const doc = (globalThis as { document?: Document }).document;
  const win = globalThis as unknown as Window;
  let storage: KeyValueStorage;
  try {
    storage = win.sessionStorage;
    storage.getItem('qd:probe');
  } catch {
    storage = memoryStorage();
  }
  let channel: BroadcastChannel | null = null;
  try {
    channel = new BroadcastChannel('quadratic');
  } catch {
    channel = null;
  }
  const listeners = new Set<(m: { type: string; accountId: string }) => void>();
  if (channel) channel.onmessage = (ev: MessageEvent) => listeners.forEach((fn) => fn(ev.data as { type: string; accountId: string }));
  return {
    timers: globalTimers(),
    fetch: (url, init) => fetch(url, init),
    socket: (url, protocols) => new WebSocket(url, protocols) as unknown as SocketLike,
    page: {
      isVisible: () => doc?.visibilityState !== 'hidden',
      isOnline: () => (globalThis as { navigator?: Navigator }).navigator?.onLine !== false,
      on(event, fn) {
        const target: EventTarget | undefined = event === 'visible' || event === 'hidden' ? doc : win;
        if (!target) return () => undefined;
        const type = event === 'visible' || event === 'hidden' ? 'visibilitychange' : event;
        const handler = (): void => {
          if (event === 'visible' && doc?.visibilityState !== 'visible') return;
          if (event === 'hidden' && doc?.visibilityState !== 'hidden') return;
          fn();
        };
        target.addEventListener(type, handler);
        return () => target.removeEventListener(type, handler);
      },
    },
    storage,
    broadcast: {
      post: (m) => channel?.postMessage(m),
      subscribe: (fn) => {
        listeners.add(fn);
        return () => listeners.delete(fn);
      },
    },
    random: () => Math.random(),
    reload: () => win.location.reload(),
  };
}
